'use client';

import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { AiBusinessProfile } from '@/lib/mockData';

export interface FixedMonthlyCosts {
  rent: number;
  payroll: number;
  utilities: number;
  software: number;
  equipment: number;
  insurance: number;
  other: number;
}

export interface BusinessProfile {
  id?: string;
  userId?: string;
  businessName: string;
  businessType: string;
  /** What the owner typed to describe their business, in their own words. */
  businessDescription?: string;
  /** Industry vocabulary the AI derived from that description. */
  aiBusinessProfile?: AiBusinessProfile | null;
  currency: string;
  trackingFrequency: 'daily' | 'weekly';
  businessGoal: 'growth' | 'profitability' | 'stability';
  goodWeekTarget?: number;
  weeklySalesGoal?: number | null;
  theme: 'light' | 'dark' | 'system';
  onboardingCompleted: boolean;
  onboardingCompletedAt?: string | null;
  onboardingGoal?: string;
  fixedMonthlyCosts: FixedMonthlyCosts;
  createdAt?: string | null;
}

const DEFAULT_FIXED_COSTS: FixedMonthlyCosts = {
  rent: 0,
  payroll: 0,
  utilities: 0,
  software: 0,
  equipment: 0,
  insurance: 0,
  other: 0,
};

const DEFAULT_PROFILE: BusinessProfile = {
  businessName: 'My Business',
  businessType: 'retail',
  currency: 'USD',
  trackingFrequency: 'weekly',
  businessGoal: 'growth',
  goodWeekTarget: undefined,
  weeklySalesGoal: undefined,
  theme: 'system',
  onboardingCompleted: false,
  onboardingCompletedAt: null,
  onboardingGoal: undefined,
  fixedMonthlyCosts: DEFAULT_FIXED_COSTS,
};

interface BusinessProfileContextValue {
  profile: BusinessProfile;
  loading: boolean;
  /**
   * Whether `profile` is a real row rather than the placeholder defaults.
   *
   * `loading` alone cannot answer this. It goes false the moment the provider
   * gives up — including when there is no signed-in user yet — and at that
   * point `profile` is still DEFAULT_PROFILE, which carries
   * `onboardingCompleted: false`. Anything that reads that flag to decide
   * where to send somebody was reading a default and treating it as an answer.
   */
  loaded: boolean;
  error: string | null;
  saveProfile: (data: Partial<BusinessProfile>) => Promise<void>;
  refreshProfile: () => Promise<void>;
  weeklyFixedCosts: number;
}

const BusinessProfileContext = createContext<BusinessProfileContextValue>({
  profile: DEFAULT_PROFILE,
  loading: true, // Start as true so consumers wait for real data
  loaded: false,
  error: null,
  saveProfile: async () => {},
  refreshProfile: async () => {},
  weeklyFixedCosts: 0,
});

export const useBusinessProfile = () => {
  const ctx = useContext(BusinessProfileContext);
  if (!ctx) throw new Error('useBusinessProfile must be used within BusinessProfileProvider');
  return ctx;
};

function rowToProfile(row: Record<string, unknown>): BusinessProfile {
  return {
    id: row.id as string,
    userId: row.user_id as string,
    businessName: (row.business_name as string) || DEFAULT_PROFILE.businessName,
    businessType: (row.business_type as string) || DEFAULT_PROFILE.businessType,
    businessDescription: (row.business_description as string) || undefined,
    aiBusinessProfile: (row.ai_business_profile as AiBusinessProfile) || null,
    currency: (row.currency as string) || DEFAULT_PROFILE.currency,
    trackingFrequency: ((row.tracking_frequency as string) || DEFAULT_PROFILE.trackingFrequency) as
      | 'daily'
      | 'weekly',
    businessGoal: ((row.business_goal as string) || DEFAULT_PROFILE.businessGoal) as
      | 'growth'
      | 'profitability'
      | 'stability',
    goodWeekTarget: row.good_week_target != null ? Number(row.good_week_target) : undefined,
    weeklySalesGoal: row.weekly_sales_goal != null ? Number(row.weekly_sales_goal) : undefined,
    theme: ((row.theme as string) || DEFAULT_PROFILE.theme) as 'light' | 'dark' | 'system',
    // Explicitly cast to boolean — never fall back to false if the DB value is true
    onboardingCompleted: row.onboarding_completed === true,
    onboardingCompletedAt: (row.onboarding_completed_at as string) || null,
    onboardingGoal: (row.onboarding_goal as string) || undefined,
    createdAt: (row.created_at as string) || null,
    fixedMonthlyCosts: {
      rent: Number(row.fixed_cost_rent ?? 0),
      payroll: Number(row.fixed_cost_payroll ?? 0),
      utilities: Number(row.fixed_cost_utilities ?? 0),
      software: Number(row.fixed_cost_software ?? 0),
      equipment: Number(row.fixed_cost_equipment ?? 0),
      insurance: Number(row.fixed_cost_insurance ?? 0),
      other: Number(row.fixed_cost_other ?? 0),
    },
  };
}

export function computeWeeklyFixedCosts(fixedMonthlyCosts: FixedMonthlyCosts): number {
  const total =
    fixedMonthlyCosts.rent +
    fixedMonthlyCosts.payroll +
    fixedMonthlyCosts.utilities +
    fixedMonthlyCosts.software +
    fixedMonthlyCosts.equipment +
    fixedMonthlyCosts.insurance +
    fixedMonthlyCosts.other;
  return total / 4.33;
}

export function BusinessProfileProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState<BusinessProfile>(DEFAULT_PROFILE);
  // Start loading=true so consumers never see the default (onboardingCompleted=false) before real data arrives
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // True until the first successful load. A later refresh must not raise
  // `loading` again: consumers read it as "there is nothing to show yet", and
  // that stops being true once there is.
  const loadedOnceRef = useRef(false);

  const fetchOrCreate = useCallback(async () => {
    if (!user) return;
    if (!loadedOnceRef.current) setLoading(true);
    setError(null);
    try {
      const supabase = createClient();
      const { data, error: fetchError } = await supabase
        .from('business_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (fetchError) {
        setError(fetchError.message);
        setLoading(false);
        return;
      }

      if (data) {
        setProfile(rowToProfile(data));
      } else {
        // Create default profile for new user
        const { data: created, error: insertError } = await supabase
          .from('business_profiles')
          .insert({
            user_id: user.id,
            business_name: DEFAULT_PROFILE.businessName,
            business_type: DEFAULT_PROFILE.businessType,
            currency: DEFAULT_PROFILE.currency,
            tracking_frequency: DEFAULT_PROFILE.trackingFrequency,
            business_goal: DEFAULT_PROFILE.businessGoal,
            good_week_target: null,
            theme: DEFAULT_PROFILE.theme,
            onboarding_completed: false,
          })
          .select('*')
          .single();

        if (insertError) {
          setError(insertError.message);
        } else if (created) {
          setProfile(rowToProfile(created));
        }
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      loadedOnceRef.current = true;
      setLoading(false);
    }
  }, [user]);

  // Keyed on the id rather than the user object. Supabase issues a new object
  // on every silent token refresh and on every tab focus; depending on the
  // object meant refetching on each of those, and the refetch raised `loading`,
  // which unmounted whatever page was open — taking any half-filled form with
  // it. The identity that matters is who is signed in, and that is the id.
  const userId = user?.id ?? null;

  useEffect(() => {
    // Wait for auth to finish loading before doing anything
    if (authLoading) return;

    if (userId) {
      void fetchOrCreate();
    } else {
      // No user — reset to defaults and stop loading
      setProfile(DEFAULT_PROFILE);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, authLoading]);

  const saveProfile = useCallback(
    async (data: Partial<BusinessProfile>) => {
      if (!user) return;
      const supabase = createClient();
      const updatePayload: Record<string, unknown> = {};
      if (data.businessName !== undefined) updatePayload.business_name = data.businessName;
      if (data.businessType !== undefined) updatePayload.business_type = data.businessType;
      if (data.businessDescription !== undefined)
        updatePayload.business_description = data.businessDescription;
      if (data.aiBusinessProfile !== undefined)
        updatePayload.ai_business_profile = data.aiBusinessProfile;
      if (data.currency !== undefined) updatePayload.currency = data.currency;
      if (data.trackingFrequency !== undefined)
        updatePayload.tracking_frequency = data.trackingFrequency;
      if (data.businessGoal !== undefined) updatePayload.business_goal = data.businessGoal;
      if (data.goodWeekTarget !== undefined)
        updatePayload.good_week_target = data.goodWeekTarget ?? null;
      if ('weeklySalesGoal' in data)
        updatePayload.weekly_sales_goal =
          data.weeklySalesGoal != null && data.weeklySalesGoal > 0 ? data.weeklySalesGoal : null;
      if (data.theme !== undefined) updatePayload.theme = data.theme;
      if (data.onboardingCompleted !== undefined)
        updatePayload.onboarding_completed = data.onboardingCompleted;
      if (data.onboardingCompleted === true && !data.onboardingCompletedAt) {
        updatePayload.onboarding_completed_at = new Date().toISOString();
      }
      if (data.onboardingCompletedAt !== undefined)
        updatePayload.onboarding_completed_at = data.onboardingCompletedAt;
      if (data.onboardingGoal !== undefined) updatePayload.onboarding_goal = data.onboardingGoal;
      if (data.fixedMonthlyCosts !== undefined) {
        updatePayload.fixed_cost_rent = data.fixedMonthlyCosts.rent ?? 0;
        updatePayload.fixed_cost_payroll = data.fixedMonthlyCosts.payroll ?? 0;
        updatePayload.fixed_cost_utilities = data.fixedMonthlyCosts.utilities ?? 0;
        updatePayload.fixed_cost_software = data.fixedMonthlyCosts.software ?? 0;
        updatePayload.fixed_cost_equipment = data.fixedMonthlyCosts.equipment ?? 0;
        updatePayload.fixed_cost_insurance = data.fixedMonthlyCosts.insurance ?? 0;
        updatePayload.fixed_cost_other = data.fixedMonthlyCosts.other ?? 0;
      }

      const { data: updated, error: updateError } = await supabase
        .from('business_profiles')
        .update(updatePayload)
        .eq('user_id', user.id)
        .select('*')
        .single();

      if (updateError) throw new Error(updateError.message);
      if (updated) setProfile(rowToProfile(updated));
    },
    [user]
  );

  const refreshProfile = useCallback(async () => {
    await fetchOrCreate();
  }, [fetchOrCreate]);

  const weeklyFixedCosts = computeWeeklyFixedCosts(profile.fixedMonthlyCosts);

  return (
    <BusinessProfileContext.Provider
      value={{
        profile,
        loading,
        // A row from the database always has an id; DEFAULT_PROFILE never does.
        loaded: Boolean(profile.id),
        error,
        saveProfile,
        refreshProfile,
        weeklyFixedCosts,
      }}
    >
      {children}
    </BusinessProfileContext.Provider>
  );
}
